import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { TicketMetricsData } from '../../models/ticket-metrics-data.model';
import { formatCurrency, formatNumber } from '../../utils/format.util';
import { GetEventRevenueMetricResponseDto } from '@smatchy/sdk';
import { EventEarningsService } from '../../services/event-earnings.service';
import { ActivityDetailDrawerComponent } from '../activity-detail-drawer/activity-detail-drawer.component';
import { CommonTableComponent, CommonTableColumn, CommonTableFilter } from '../common-table/common-table.component';
import { RevenueStoreService } from '../../../services/revenue/revenue.store.service';
import { ActivityInfoCellComponent } from '../common-table/activity-info-cell/activity-info-cell.component';
import { UserInfoCellComponent } from '../common-table/user-info-cell/user-info-cell.component';
import { CurrencyCellComponent } from '../common-table/currency-cell/currency-cell.component';
import { StatusCellComponent } from '../common-table/status-cell/status-cell.component';
import { SportsService } from '@smatchy/sdk';
import { SportsUiService } from '../../services/sports.service';

@Component({
  selector: 'bo-ticket-metrics-by-event',
  templateUrl: './ticket-metrics-by-event.component.html',
  imports: [
    TranslateModule,
    CommonModule,
    FormsModule,
    TableModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    DropdownModule,
    ReactiveFormsModule,
    ButtonModule,
    RippleModule,
    TooltipModule,
    ActivityDetailDrawerComponent,
    CommonTableComponent,
    ActivityInfoCellComponent,
    UserInfoCellComponent,
    CurrencyCellComponent,
    StatusCellComponent,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./ticket-metrics-by-event.component.scss'],
})
export class TicketMetricsByEventComponent implements OnInit, OnChanges, OnDestroy {
  #cdr = inject(ChangeDetectorRef);
  #translate = inject(TranslateService);
  #destroyRef = inject(DestroyRef);
  #activityDetailService = inject(EventEarningsService);
  #sportsService = inject(SportsService);
  #sportsUi = inject(SportsUiService);
  @Input() title = 'financePages.tmTitle';
  @Input() data: TicketMetricsData[] = [];
  @Input() startDate?: string;
  @Input() endDate?: string;
  drawerVisible = signal<boolean>(false);
  selectedActivity = signal<GetEventRevenueMetricResponseDto | null>(null);

  #revenueStore = inject(RevenueStoreService);
  
  @ViewChild(CommonTableComponent) commonTable?: CommonTableComponent;
  
  isFilterExpanded = signal<boolean>(false);
  dataSignal = signal<TicketMetricsData[]>([]);
  totalRecords = this.#revenueStore.eventMetricsTotal;
  currentPage = this.#revenueStore.eventMetricsPage;
  currentLimit = this.#revenueStore.eventMetricsLimit;
  
  searchValue = signal<string>('');
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(0);
  private searchSubject = new Subject<string>();

  tableFilters: CommonTableFilter[] = [
    {
      field: 'sport',
      label: 'Sport',
      options: [{ label: 'All sport', value: null }],
      placeholder: 'All sport',
    },
    {
      field: 'status',
      label: 'Status',
      options: [
        { label: 'All status', value: null },
        { label: 'Transferred', value: 'paid' },
        // { label: 'Cancelled', value: 'cancelled' },
        { label: 'Incoming', value: 'incoming' },
        { label: 'Awaiting transfer', value: 'pending_payout' },
        { label: 'Available', value: 'available' },
      ],
      placeholder: 'All status',
    },
  ];

  filterValues: Record<string, string | null> = {
    sport: null,
    status: null,
  };

  private searchSubscription?: Subscription;

  ngOnInit(): void {
    this.applyFilterTranslations();
    this.#translate.onLangChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.applyFilterTranslations());
    this.loadSports();
    this.searchSubscription = this.searchSubject.pipe(debounceTime(300)).subscribe((search: string) => {
      this.loadMetrics({ search, page: 1 });
    });
    
    if (!this.data || this.data.length === 0) {
      this.loadMetrics({ page: 1, limit: 25 });
    } else {
      
      this.dataSignal.set(this.data || []);
    }
  }

  /**
   * Localizes the static filter labels in place, without touching the sport
   * options that load in asynchronously, so a language change never wipes the
   * fetched sport list. Status options are a fixed list, so they are relabelled
   * by position.
   */
  private applyFilterTranslations(): void {
    const t = (key: string) => this.#translate.instant(key);
    const sportFilter = this.tableFilters.find((f) => f.field === 'sport');
    if (sportFilter) {
      sportFilter.label = t('financePages.tmSport');
      sportFilter.placeholder = t('financePages.tmAllSport');
      if (sportFilter.options[0]) {
        sportFilter.options[0].label = t('financePages.tmAllSport');
      }
    }
    const statusFilter = this.tableFilters.find((f) => f.field === 'status');
    if (statusFilter) {
      statusFilter.label = t('financePages.tmStatus');
      statusFilter.placeholder = t('financePages.tmAllStatus');
      const statusKeys = [
        'financePages.tmAllStatus',
        'statusLabels.transferred',
        'statusLabels.incoming',
        'statusLabels.awaitingTransfer',
        'statusLabels.available',
      ];
      statusFilter.options.forEach((option, index) => {
        if (statusKeys[index]) {
          option.label = t(statusKeys[index]);
        }
      });
    }
    this.tableColumns = this.buildColumns();
    this.#cdr.markForCheck();
  }

  private loadSports(): void {
    this.#sportsUi.getActiveSportOptions('en').subscribe({
      next: (options) => {
        const sportFilter = this.tableFilters.find((f) => f.field === 'sport');
        if (sportFilter) {
          sportFilter.options = options;
          this.#cdr.markForCheck();
        }
      },
      error: (err) => {
        console.warn('Failed to load sports:', err);
      },
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.dataSignal.set(this.data || []);
      this.#cdr.markForCheck();
    }
    if (changes['startDate'] || changes['endDate']) {
      this.loadMetrics({ page: 1 });
    }
  }

  onFilterChange(event: { field: string; value: string | number | null | undefined }): void {
    const value = event.value as string | null;
    this.filterValues = { ...(this.filterValues ?? {}), [event.field]: value };
    this.loadMetrics();
    this.#cdr.markForCheck();
  }

  onClearFilters(): void {
    this.filterValues = { sport: null, status: null };
    this.searchValue.set('');
    this.loadMetrics();
    this.#cdr.markForCheck();
  }

  toggleFilters(): void {
    this.isFilterExpanded.update((value) => !value);
  }

  onSortChangeRequested(event: { sortBy?: string; sortOrder?: 'asc' | 'desc' }): void {
    this.loadMetrics({ 
      sortBy: event.sortBy as 'eventTitle' | 'organizerName' | 'ticketPrice' | 'eventRevenue' | 'refunds' | 'finalEventRevenue' | 'organizerRevenue' | 'smatchyRevenue' | 'status' | 'revenue' | 'ticketsSold' | 'eventDate' | undefined,
      sortOrder: event.sortOrder 
    });
    this.#cdr.markForCheck();
  }

  onPageChangeRequested(event: { page: number; limit: number }): void {
    this.loadMetrics({ page: event.page, limit: event.limit });
    this.#cdr.markForCheck();
  }
  
  mapFieldToSortBy = (field: string): string | undefined => {
    switch (field) {
      case 'event':
        return 'eventTitle';
      case 'organizer':
        return 'organizerName';
      case 'ticketPrice':
        return 'ticketPrice';
      case 'eventRevenue':
        return 'eventRevenue';
      case 'refunds':
        return 'refunds';
      case 'finalEventRevenue':
        return 'finalEventRevenue';
      case 'organizerRevenue':
        return 'organizerRevenue';
      case 'smatchyRevenue':
        return 'smatchyRevenue';
      case 'status':
        return 'status';
      case 'ticketsSold':
      case 'ticketsProcessed':
        return 'ticketsSold';
      case 'eventDate':
        return 'eventDate';
      default:
        return undefined;
    }
  };

  onGlobalFilterChange(value: string): void {
    this.searchValue.set(value);
    this.searchSubject.next(value);
    this.#cdr.markForCheck();
  }

  private loadMetrics(updates?: {
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: 'eventTitle' | 'organizerName' | 'ticketPrice' | 'eventRevenue' | 'refunds' | 'finalEventRevenue' | 'organizerRevenue' | 'smatchyRevenue' | 'status' | 'revenue' | 'ticketsSold' | 'eventDate';
    sortOrder?: 'asc' | 'desc';
  }): void {
    type SortByType = 'eventTitle' | 'organizerName' | 'ticketPrice' | 'eventRevenue' | 'refunds' | 'finalEventRevenue' | 'organizerRevenue' | 'smatchyRevenue' | 'status' | 'revenue' | 'ticketsSold' | 'eventDate';
    const filters = {
      startDate: this.startDate,
      endDate: this.endDate,
      search: updates?.search !== undefined ? updates.search : this.searchValue(),
      page: updates?.page !== undefined ? updates.page : this.currentPage(),
      limit: updates?.limit !== undefined ? updates.limit : this.currentLimit(),
      status: this.filterValues['status'] || undefined,
      sport: this.filterValues['sport'] || undefined,
      sortBy: updates?.sortBy as SortByType | undefined,
      sortOrder: updates?.sortOrder,
    };
    this.#revenueStore.getEventRevenueMetrics(filters);    
  }


  formatDate(date: string | Date): string {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  formatCurrency(value: number): string {
    return formatCurrency(value);
  }

  formatNumber(value: number): string {
    return formatNumber(value);
  }

  tableColumns: CommonTableColumn[] = [];

  private buildColumns(): CommonTableColumn[] {
    const t = (key: string) => this.#translate.instant(key);
    return [
      { label: t('financePages.txColActivity'), field: 'event', sortable: true, width: '15%', minWidth: '150px' },
      { label: t('financePages.payoutColOrganizer'), field: 'organizer', sortable: true, width: '12%' },
      { label: t('financePages.tmTicketPrice'), field: 'ticketPrice', sortable: true, width: '0%' },
      { label: t('financePages.esColEventRevenue'), field: 'eventRevenue', sortable: true, width: '0%', minWidth: '115px' },
      { label: t('financePages.adRefundProcessed'), field: 'refunds', sortable: true, width: '0%' },
      { label: t('financePages.bdColFinalEventRevenue'), field: 'finalEventRevenue', sortable: true, width: '0%', minWidth: '115px' },
      { label: t('financePages.bdColOrganizerRevenue'), field: 'organizerRevenue', sortable: true, width: '0%' },
      { label: t('financePages.bdColFinalSmatchyRevenue'), field: 'smatchyRevenue', sortable: true, width: '0%', minWidth: '135px' },
      { label: t('financePages.bdEarningsStatus'), field: 'status', sortable: true, width: '1%', minWidth: '100px' },
    ];
  }

  /** Subtitles under the ticket counts, localized with the count interpolated. */
  ticketsProcessedLabel(count: number): string {
    return this.#translate.instant('financePages.tmTicketsProcessed', { count });
  }
  ticketRefundLabel(count: number | undefined): string {
    const n = count ?? 0;
    return this.#translate.instant(n === 1 ? 'financePages.tmTicketRefundOne' : 'financePages.tmTicketRefundMany', { count: n });
  }
  ticketsSoldLabel(count: number): string {
    return this.#translate.instant('financePages.tmTicketsSold', { count });
  }

  onRowClick(metric: TicketMetricsData): void {
    this.#activityDetailService.getActivityDetail(metric.id).subscribe((activity) => {
      this.selectedActivity.set(activity);
      this.drawerVisible.set(true);
      this.#cdr.markForCheck();
    });
  }

  onDrawerClose(): void {
    this.drawerVisible.set(false);
  }

}
